import { useQuery } from '@tanstack/react-query';
import { api, must } from './api';

/**
 * Le due liste che quasi ogni schermata consulta: server e istanze.
 *
 * Stanno qui e non nelle rotte perché si leggono a vicenda — la lista dei
 * server dice quante istanze ospita ciascuno, quella delle istanze su quale
 * server gira ciascuna — e due rotte che si importano l'un l'altra sono un
 * ciclo che prima o poi morde.
 */

/** Gli stati in cui il provisioning sta ancora lavorando: la lista si aggiorna da sola. */
export const SERVER_IN_CORSO = new Set(['creating', 'waiting_ip', 'waiting_ssh', 'bootstrapping', 'installing_stack', 'verifying', 'decommissioning']);

/** Gli stati di un'istanza in cui l'hub sta lavorando. */
export const ISTANZA_IN_CORSO = new Set(['creating', 'starting', 'stopping', 'restarting', 'modifying', 'restoring', 'deleting']);

export function useServers(slug: string) {
  return useQuery({
    queryKey: ['servers', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/servers', { params: { path: { slug } } })),
    // Finché un server si sta costruendo la lista si aggiorna da sola. Il
    // dettaglio riga per riga è nello stream SSE del job; qui basta lo stato.
    refetchInterval: (q) =>
      (q.state.data?.servers ?? []).some((s) => SERVER_IN_CORSO.has(s.status)) ? 3000 : false,
  });
}

export function useIstanze(slug: string) {
  return useQuery({
    queryKey: ['instances', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/instances', { params: { path: { slug } } })),
    refetchInterval: (q) =>
      (q.state.data?.instances ?? []).some((i) => ISTANZA_IN_CORSO.has(i.status)) ? 3000 : false,
  });
}

export type Avviso = NonNullable<ReturnType<typeof useAvvisi>['data']>[number];
type Avvisi = ReturnType<typeof useAvvisi>['data'];

/**
 * Cosa non va adesso. Ogni avviso appartiene a un server (`serverId`): la
 * lista lo mostra sulla scheda della macchina e il dettaglio lo spiega, così
 * chi guarda non deve andarselo a cercare — e non trova nove banner uguali
 * prima dei server.
 */
export function useAvvisi(slug: string) {
  return useQuery({
    queryKey: ['alerts', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/alerts', { params: { path: { slug } } })),
    refetchInterval: 60_000,
  });
}

/** Gli avvisi di un server: prima quelli accesi, poi quelli da tenere d'occhio. */
export function avvisiDelServer(avvisi: Avvisi, serverId: string) {
  const suoi = (avvisi ?? []).filter((a) => a.serverId === serverId);
  return { accesi: suoi.filter((a) => a.state === 'firing'), inArrivo: suoi.filter((a) => a.state !== 'firing') };
}

/** «Certificato in scadenza ×3 · Il server non riporta più»: i titoli, e quante volte. */
export function riassuntoAvvisi(avvisi: Avviso[]): string {
  const conta = new Map<string, number>();
  for (const a of avvisi) conta.set(a.title, (conta.get(a.title) ?? 0) + 1);
  return [...conta].map(([titolo, n]) => (n > 1 ? `${titolo} ×${n}` : titolo)).join(' · ');
}
